import { create } from 'zustand';
import axios from 'axios';
import type { AuthResponse, User } from '@opus/types';
import { createApiClient, setUnauthorizedHandler, singleFlight } from './api';

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  user: User | null;
  /** access token lives in MEMORY ONLY — the httpOnly refresh cookie is the persistence (brief 002-001-001 §1) */
  accessToken: string | null;
  status: AuthStatus;
  setSession: (user: User, accessToken: string) => void;
  clear: () => void;
  /** silent refresh on boot: unknown → authenticated | anonymous exactly once */
  bootstrap: () => Promise<void>;
}

// plain axios (no interceptors) so the refresh call cannot recurse through the client
const refreshAxios = axios.create({ baseURL: '/api/v1', timeout: 15_000 });

const doRefresh = async (): Promise<string | null> => {
  try {
    // the api answers 200 {authenticated:false} for anonymous visitors — no console noise
    const res = await refreshAxios.post<{ data: AuthResponse & { authenticated?: boolean } }>('/auth/refresh');
    if (res.data?.data?.authenticated === false || !res.data?.data?.accessToken) {
      useAuthStore.getState().clear();
      return null;
    }
    useAuthStore.getState().setSession(res.data.data.user, res.data.data.accessToken);
    return res.data.data.accessToken;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
};

// one shared refresh across every concurrent 401 (the audit's deadlock, structurally removed)
const refreshSingleFlight = singleFlight(doRefresh);

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  accessToken: null,
  status: 'unknown',
  setSession: (user, accessToken) => set({ user, accessToken, status: 'authenticated' }),
  clear: () => set({ user: null, accessToken: null, status: 'anonymous' }),
  bootstrap: async () => {
    if (useAuthStore.getState().status !== 'unknown') return;
    await refreshSingleFlight();
  },
}));

// wire the api client to the store
export const api = createApiClient(
  () => useAuthStore.getState().accessToken,
  () => refreshSingleFlight(),
);
setUnauthorizedHandler(() => useAuthStore.getState().clear());

const EMPTY_PERMISSIONS: string[] = [];

/** Stable snapshot — never allocate inside the selector (React 19 useSyncExternalStore). */
export function usePermissions(): string[] {
  return useAuthStore((s) => s.user?.permissions ?? EMPTY_PERMISSIONS);
}

export { refreshSingleFlight };
